import { useState, useEffect } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { cx } from '@utils/cx'
import projectsData from '@data/projects.json'
import styles from './ProjectsGallery.module.css'

// Utility function to shuffle array
const shuffleArray = (array) => {
  const result = [...array]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export default function ProjectsGallery ({ onProjectClick }) {
  const { t } = useLanguage()
  const [projects, setProjects] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Load and shuffle projects on mount
  useEffect(() => {
    const projectsArray = Object.values(projectsData.projects)
    const shuffledProjects = shuffleArray(projectsArray)
    setProjects(shuffledProjects)
  }, [])

  // Auto-rotation every 4 seconds
  useEffect(() => {
    if (projects.length === 0) return

    const interval = setInterval(() => {
      handleNext()
    }, 4000)

    return () => clearInterval(interval)
  }, [projects, currentIndex])

  const handleNext = () => {
    if (isTransitioning) return

    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % projects.length)
      setIsTransitioning(false)
    }, 150) // Half of transition duration
  }

  const handlePrev = () => {
    if (isTransitioning) return

    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length)
      setIsTransitioning(false)
    }, 150)
  }

  const handleProjectClick = () => {
    if (projects[currentIndex] && onProjectClick) {
      onProjectClick(projects[currentIndex])
    }
  }

  const handleIndicatorClick = (index) => {
    if (isTransitioning) return

    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex(index)
      setIsTransitioning(false)
    }, 150)
  }

  if (projects.length === 0) {
    return (
      <div className={styles.projectsGallery}>
        <div className={styles.loading}>
          <div className={styles.loadingLogo} />
          <p className={styles.loadingText}>{t.core.projects.loading}</p>
        </div>
      </div>
    )
  }

  const currentProject = projects[currentIndex]

  return (
    <div className={styles.projectsGallery}>
      {/* Main Project Display */}
      <div className={styles.display}>
        <button
          className={styles.project}
          onClick={handleProjectClick}
          aria-label={t.core.access.projects.viewDetails.replace('{name}', currentProject.name)}
        >
          <div className={cx(
            styles.logoContainer,
            isTransitioning && styles.logoContainerTransitioning
          )}
          >
            <img
              src={currentProject.logo}
              alt={t.core.access.projects.logo.replace('{name}', currentProject.name)}
              className={styles.logo}
              loading='lazy'
              width='200'
              height='200'
            />
          </div>
          <h3 className={styles.name}>{currentProject.name}</h3>
        </button>
      </div>

      {/* Navigation Controls */}
      <div className={styles.controls}>
        <button
          className={styles.nav}
          onClick={handlePrev}
          aria-label={t.core.access.projects.previous}
          disabled={isTransitioning}
        >
          ‹
        </button>

        <div className={styles.indicators}>
          {projects.map((_, index) => (
            <button
              key={index}
              className={cx(
                styles.indicator,
                index === currentIndex && styles.indicatorActive
              )}
              onClick={() => { handleIndicatorClick(index) }}
              aria-label={t.core.access.projects.goTo.replace('{number}', index + 1)}
            />
          ))}
        </div>

        <button
          className={styles.nav}
          onClick={handleNext}
          aria-label={t.core.access.projects.next}
          disabled={isTransitioning}
        >
          ›
        </button>
      </div>

      {/* Project Info */}
      <div className={styles.info}>
        <p className={styles.counter}>
          {t.core.projects.counter
            .replace('{current}', currentIndex + 1)
            .replace('{total}', projects.length)}
        </p>
        {currentProject.url && (
          <a
            href={currentProject.url}
            target='_blank'
            rel='noopener noreferrer'
            className='projects-gallery__link'
            aria-label={t.core.access.projects.visitSite.replace('{name}', currentProject.name)}
          >
            {t.core.projects.visitSite}
          </a>
        )}
        {currentProject.social?.ig && (
          <a
            href={`https://instagram.com/${currentProject.social.ig}`}
            target='_blank'
            rel='noopener noreferrer'
            className={styles.social}
            aria-label={t.core.access.projects.followInstagram.replace('{name}', currentProject.name)}
          >
            @{currentProject.social.ig}
          </a>
        )}
      </div>
    </div>
  )
}
